import React, { useState, useEffect } from 'react';

const ValidationAssistant = ({ item, onSubmit, onCancel }) => {
  const [step, setStep] = useState(1);  // 1: intro, 2: questions, 3: alternatives, 4: confirm
  const [specs, setSpecs] = useState({});
  const [selectedAlt, setSelectedAlt] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Charger suggestions au démarrage
  useEffect(() => {
    if (step === 3) {
      loadSuggestions();
    }
  }, [step]);

  const loadSuggestions = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('https://api.aimycore.fr/api/analyze/suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          equipment_text: item.label || '',
          extracted_specs: item.specs || {},
          match_ref: item.match_ref
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.primary_suggestion) {
          setSuggestions([data.primary_suggestion, ...(data.alternatives || [])]);
        }
      }
    } catch (error) {
      console.error('Erreur chargement suggestions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextStep = () => {
    if (step < 4) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleFinish = async () => {
    // Soumettre feedback enrichi
    const feedbackData = {
      item_id: item.item_id || `item_${Date.now()}`,
      feedback_type: selectedAlt ? 'correct' : 'partial',
      equipment_text: item.label || '',
      match_ref: selectedAlt ? selectedAlt.ref : item.match_ref,
      comment: `Validation assistée: ${selectedAlt ? 'Alternative sélectionnée: ' + selectedAlt.ref : 'Match partiel validé'}`,
      timestamp: new Date().toISOString(),
      extracted_specs: item.specs || {},
      validation_confidence: 0.85,
      user_input: {
        assistant_mode: true,
        selected_alternative: selectedAlt ? selectedAlt.ref : null,
        specs_validated: Object.keys(specs).length > 0 ? specs : {}
      }
    };

    try {
      await fetch('https://api.aimycore.fr/api/analyze/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedbackData)
      });

      if (onSubmit) {
        onSubmit(feedbackData);
      } else {
        alert('✅ Validation assistée enregistrée !');
      }
    } catch (error) {
      alert(`❌ Erreur: ${error.message}`);
    }
  };

  return (
    <div style={styles.modal}>
      <div style={styles.container}>
        {/* Header avec progress indicator */}
        <div style={styles.header}>
          <h2>🚀 Validation Assistée</h2>
          <div style={styles.progress}>
            {'●'.repeat(step)}{'○'.repeat(4 - step)}
          </div>
          <button onClick={onCancel} style={styles.closeBtn}>✕</button>
        </div>

        {/* Step 1: Introduction */}
        {step === 1 && (
          <StepIntro item={item} onNext={handleNextStep} />
        )}

        {/* Step 2: Questions guidées */}
        {step === 2 && (
          <StepQuestions
            item={item}
            specs={specs}
            onSpecsChange={setSpecs}
            onNext={handleNextStep}
            onBack={handleBack}
          />
        )}

        {/* Step 3: Alternatives */}
        {step === 3 && (
          <StepAlternatives
            isLoading={isLoading}
            suggestions={suggestions}
            selectedAlt={selectedAlt}
            onSelect={setSelectedAlt}
            onNext={handleNextStep}
            onBack={handleBack}
          />
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && (
          <StepConfirm
            item={item}
            selectedAlt={selectedAlt}
            onConfirm={handleFinish}
            onBack={handleBack}
          />
        )}
      </div>
    </div>
  );
};

const StepIntro = ({ item, onNext }) => (
  <div style={styles.step}>
    <div style={styles.stepTitle}>📋 Résumé extraction</div>
    <div style={styles.infoBox}>
      <p><strong>Équipement:</strong> {item.label}</p>
      <p><strong>Catégorie:</strong> {item.specs?.category || 'N/A'}</p>
      {item.specs?.brand && <p><strong>Marque détectée:</strong> {item.specs.brand}</p>}
      {item.specs?.type && <p><strong>Type:</strong> {item.specs.type}</p>}
      {item.match_label && <p><strong>Match proposé:</strong> {item.match_label}</p>}
    </div>
    <p style={styles.helpText}>
      🎯 Ce mode vous guide pour valider rapidement l'extraction et confirmer les alternatives.
    </p>
    <button onClick={onNext} style={styles.primaryBtn}>
      Continuer →
    </button>
  </div>
);

const StepQuestions = ({ item, specs, onSpecsChange, onNext, onBack }) => (
  <div style={styles.step}>
    <div style={styles.stepTitle}>🤔 Questions rapides</div>

    <div style={styles.question}>
      <p><strong>1️⃣  Marque correcte?</strong></p>
      <div style={styles.answerGroup}>
        <input
          type="text"
          value={specs.brand || item.specs?.brand || ''}
          onChange={(e) => onSpecsChange({ ...specs, brand: e.target.value })}
          placeholder="Ex: Shure, CODA, Chauvet..."
          style={styles.input}
        />
      </div>
    </div>

    <div style={styles.question}>
      <p><strong>2️⃣  Type d'équipement</strong></p>
      <div style={styles.answerGroup}>
        <input
          type="text"
          value={specs.type || item.specs?.type || ''}
          onChange={(e) => onSpecsChange({ ...specs, type: e.target.value })}
          placeholder="Ex: Microphone HF, Projecteur LED..."
          style={styles.input}
        />
      </div>
    </div>

    {item.specs?.power_w && (
      <div style={styles.question}>
        <p><strong>3️⃣  Puissance (W)</strong></p>
        <div style={styles.answerGroup}>
          <input
            type="number"
            value={specs.power_w || item.specs.power_w || ''}
            onChange={(e) => onSpecsChange({ ...specs, power_w: e.target.value })}
            placeholder="Ex: 300"
            style={styles.input}
          />
        </div>
      </div>
    )}

    {item.specs?.coverage_h && (
      <div style={styles.question}>
        <p><strong>4️⃣  Angle couverture (°)</strong></p>
        <div style={styles.answerGroup}>
          <input
            type="number"
            value={specs.coverage_h || item.specs.coverage_h || ''}
            onChange={(e) => onSpecsChange({ ...specs, coverage_h: e.target.value })}
            placeholder="Ex: 60"
            style={styles.input}
          />
        </div>
      </div>
    )}

    <div style={styles.actions}>
      <button onClick={onBack} style={styles.secondaryBtn}>← Retour</button>
      <button onClick={onNext} style={styles.primaryBtn}>Continuer →</button>
    </div>
  </div>
);

const StepAlternatives = ({ isLoading, suggestions, selectedAlt, onSelect, onNext, onBack }) => (
  <div style={styles.step}>
    <div style={styles.stepTitle}>🔄 Alternatives détectées</div>

    {isLoading && <p style={styles.loadingText}>⏳ Calcul des alternatives...</p>}

    {!isLoading && suggestions.length === 0 && (
      <p style={styles.noMatch}>Aucune alternative trouvée. Continuer avec le match actuel?</p>
    )}

    {suggestions.length > 0 && (
      <div style={styles.suggestionsGrid}>
        {suggestions.map((alt, idx) => (
          <div
            key={idx}
            style={{
              ...styles.suggestionCard,
              borderColor: selectedAlt?.ref === alt.ref ? '#0284c7' : '#e5e7eb',
              backgroundColor: selectedAlt?.ref === alt.ref ? '#f0f9ff' : '#ffffff'
            }}
            onClick={() => onSelect(alt)}
          >
            <div style={styles.suggestionHeader}>
              <strong>{alt.ref}</strong>
              <span style={styles.scoreTag}>{(alt.score * 100).toFixed(0)}%</span>
            </div>
            <p style={styles.suggestionLabel}>{alt.label}</p>
            {alt.price && <p style={styles.suggestionPrice}>€{alt.price}/jour</p>}
            {alt.compatibility_notes && alt.compatibility_notes.length > 0 && (
              <ul style={styles.notesList}>
                {alt.compatibility_notes.slice(0, 2).map((note, i) => (
                  <li key={i} style={styles.notesItem}>{note}</li>
                ))}
              </ul>
            )}
            <div style={{
              textAlign: 'center',
              marginTop: '8px',
              fontSize: '12px',
              color: selectedAlt?.ref === alt.ref ? '#0284c7' : '#999'
            }}>
              {selectedAlt?.ref === alt.ref ? '✓ Sélectionné' : 'Cliquer pour sélectionner'}
            </div>
          </div>
        ))}
      </div>
    )}

    <div style={styles.actions}>
      <button onClick={onBack} style={styles.secondaryBtn}>← Retour</button>
      <button onClick={onNext} style={styles.primaryBtn}>Confirmer →</button>
    </div>
  </div>
);

const StepConfirm = ({ item, selectedAlt, onConfirm, onBack }) => (
  <div style={styles.step}>
    <div style={styles.stepTitle}>✅ Confirmation</div>

    <div style={styles.confirmBox}>
      <p><strong>Équipement extrait:</strong> {item.label}</p>
      {selectedAlt ? (
        <>
          <p><strong>Alternative choisie:</strong></p>
          <div style={styles.selectedAltBox}>
            <div><strong>{selectedAlt.ref}</strong></div>
            <div style={{ fontSize: '13px', color: '#666' }}>{selectedAlt.label}</div>
            {selectedAlt.price && <div style={{ fontSize: '12px', color: '#059669' }}>€{selectedAlt.price}/jour</div>}
          </div>
        </>
      ) : (
        <p><strong>Match actuel confirmé</strong></p>
      )}
    </div>

    <p style={styles.successText}>
      🎉 Feedback enregistré pour améliorer le matching LLM!
    </p>

    <div style={styles.actions}>
      <button onClick={onBack} style={styles.secondaryBtn}>← Retour</button>
      <button onClick={onConfirm} style={styles.successBtn}>Terminer ✓</button>
    </div>
  </div>
);

const styles = {
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    fontFamily: 'system-ui, -apple-system, sans-serif'
  },
  container: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '550px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)'
  },
  header: {
    padding: '20px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  progress: {
    fontSize: '14px',
    color: '#0284c7',
    letterSpacing: '3px'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#999'
  },
  step: {
    padding: '20px'
  },
  stepTitle: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '15px',
    color: '#1f2937'
  },
  infoBox: {
    backgroundColor: '#f0f9ff',
    border: '1px solid #bfdbfe',
    borderRadius: '6px',
    padding: '12px',
    marginBottom: '15px',
    fontSize: '13px'
  },
  question: {
    marginBottom: '15px'
  },
  answerGroup: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px'
  },
  input: {
    flex: 1,
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '13px'
  },
  helpText: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '15px',
    fontStyle: 'italic'
  },
  loadingText: {
    textAlign: 'center',
    color: '#999',
    padding: '20px'
  },
  noMatch: {
    backgroundColor: '#fef3c7',
    border: '1px solid #fcd34d',
    borderRadius: '4px',
    padding: '10px',
    color: '#92400e',
    fontSize: '13px'
  },
  suggestionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
    marginBottom: '15px'
  },
  suggestionCard: {
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: '#ffffff'
  },
  suggestionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '5px',
    fontSize: '13px'
  },
  scoreTag: {
    backgroundColor: '#dbeafe',
    color: '#0284c7',
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '11px',
    fontWeight: '600'
  },
  suggestionLabel: {
    fontSize: '12px',
    color: '#555',
    margin: '5px 0'
  },
  suggestionPrice: {
    fontSize: '11px',
    color: '#059669',
    fontWeight: '600',
    margin: '3px 0 5px 0'
  },
  notesList: {
    fontSize: '11px',
    color: '#666',
    marginTop: '5px',
    paddingLeft: '15px',
    margin: '5px 0 0 0'
  },
  notesItem: {
    margin: '2px 0'
  },
  confirmBox: {
    backgroundColor: '#ecfdf5',
    border: '1px solid #a7f3d0',
    borderRadius: '6px',
    padding: '12px',
    marginBottom: '15px',
    fontSize: '13px'
  },
  selectedAltBox: {
    backgroundColor: '#f0f9ff',
    border: '1px solid #bfdbfe',
    borderRadius: '4px',
    padding: '10px',
    marginTop: '8px',
    fontSize: '12px'
  },
  successText: {
    backgroundColor: '#fef3c7',
    border: '1px solid #fcd34d',
    borderRadius: '4px',
    padding: '10px',
    fontSize: '13px',
    color: '#92400e',
    marginBottom: '15px',
    textAlign: 'center'
  },
  actions: {
    display: 'flex',
    gap: '10px',
    marginTop: '20px',
    justifyContent: 'flex-end'
  },
  primaryBtn: {
    padding: '10px 20px',
    backgroundColor: '#0284c7',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600'
  },
  secondaryBtn: {
    padding: '10px 20px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px'
  },
  successBtn: {
    padding: '10px 20px',
    backgroundColor: '#059669',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600'
  }
};

export default ValidationAssistant;
